// import { toast } from "react-toastify";
import { normalizeExtraInfoValue, isNewFormat, isSpecialType, formatNumberText } from "../extraInfo.utils";
import * as tagSelectors from "../../../../shared/db/tags-labels-selectors";
import { ItemType } from "../../../../shared/interfaces";

export function copyDeviceExtraInfo(
  labels: [string, any][],
  infos: any,
  authUser: any,
  isSuperAdmin: boolean,
  currentLabels?: Record<string, string>,
  options?: { itemName?: string; itemType?: ItemType }
) {
  const header =
    options?.itemName && options?.itemType === ItemType.device
      ? `Vehicle: ${options.itemName}`
      : "";

  const body = labels
    .filter(([name]) => {
      if (name.toLowerCase() === "gps") return false;
      const labelId = tagSelectors.mapNameToIdValue(name, infos) || name;
      const userHasPermission = infos[labelId]?.details?.isHidden
        ? isSuperAdmin || infos[labelId]?.details?.author === authUser?.uid
        : true;
      return userHasPermission;
    })
    .map(([name, value]) => {
      const isCategory = isSpecialType(value, "isCategory");
      const hasNew = isNewFormat(value);
      let line = "";

      if (isCategory) {
        const entries = Object.entries(value)
          .filter(([key]) => {
            if (key === "isCategory") return false;
            const userHasPermission = infos[key]?.details?.isHidden
              ? isSuperAdmin || infos[key]?.details?.author === authUser?.uid
              : true;
            const isSerialNumber = key === "0" && value.isCategory;
            return userHasPermission && !isSerialNumber;
          })
          .map(([id, val]) => {
            let label = id;
            if (currentLabels && currentLabels[id]) label = currentLabels[id];
            else if (infos?.[id]?.details?.name) label = infos[id].details.name;

            const { text, date, files } = normalizeExtraInfoValue(val);
            const labelIdForVal = tagSelectors.mapNameToIdValue(id, infos) || id;
            const valueType = (infos[labelIdForVal]?.details as any)?.valueType;
            const formattedText = valueType === 'money' ? formatNumberText(text) : text;
            let valueContent: any = formattedText;
            if (valueType === "money" && formattedText !== undefined && formattedText !== null) {
              valueContent = `$${formattedText}`;
            }
            const parts: string[] = [];
            if (valueContent !== undefined && valueContent !== null) parts.push(String(valueContent));
            if (date) parts.push(String(date));
            if (files?.length) {
              const head = files[0]?.format === "pdf" ? "[PDF]" : "[Image]";
              parts.push(files.length > 1 ? `${head}(+${files.length - 1})` : head);
            }
            return `${label}: ${parts.join(", ") || "-"}`;
          })
          .filter(Boolean);

        line = entries.join("\n");
      } else if (hasNew) {
        const { text, date, files } = normalizeExtraInfoValue(value);
        const labelIdForVal = tagSelectors.mapNameToIdValue(name, infos) || name;
        const valueType = (infos[labelIdForVal]?.details as any)?.valueType;
        const formattedText = valueType === 'money' ? formatNumberText(text) : text;
        let valueContent: any = formattedText;
        if (valueType === "money" && formattedText !== undefined && formattedText !== null) {
          valueContent = `$${formattedText}`;
        }
        const parts: string[] = [];
        if (valueContent !== undefined && valueContent !== null) parts.push(String(valueContent));
        if (date) parts.push(String(date));
        if (files?.length) {
          const head = files[0]?.format === "pdf" ? "[PDF]" : "[Image]";
          parts.push(files.length > 1 ? `${head}(+${files.length - 1})` : head);
        }
        line = `${name}: ${parts.join(", ") || "-"}`;
      } else {
        const { text, date, files } = normalizeExtraInfoValue(value);
        const labelIdForVal = tagSelectors.mapNameToIdValue(name, infos) || name;
        const valueType = (infos[labelIdForVal]?.details as any)?.valueType;
        const formattedText = valueType === 'money' ? formatNumberText(text) : text;
        let valueContent: any = formattedText;
        if (valueType === "money" && formattedText !== undefined && formattedText !== null) {
          valueContent = `$${formattedText}`;
        }
        const parts: string[] = [];
        if (valueContent !== undefined && valueContent !== null) parts.push(String(valueContent));
        if (date) parts.push(String(date));
        if (files?.length) {
          const head = files[0]?.format === "pdf" ? "[PDF]" : "[Image]";
          parts.push(files.length > 1 ? `${head}(+${files.length - 1})` : head);
        }
        line = `${name}: ${parts.join(", ") || "-"}`;
      }
      return line;
    })
    .join("\n");

  const text = [header, body].filter(Boolean).join("\n");

  navigator.clipboard
    .writeText(text)
    .then(() => console.log("Copied to clipboard!"))
    .catch((err) => console.log("Failed to copy: " + err));
}

export function exportDeviceExtraInfoCsv(
  labels: [string, any][],
  infos: any,
  authUser: any,
  isSuperAdmin: boolean,
  currentLabels?: Record<string, string>
) {
  const hasNewFormat = labels.some(([_, value]) => isNewFormat(value));
  const headers = ["Label", "Value", ...(hasNewFormat ? ["Date", "Files"] : [])];
  const rows: string[][] = [headers];

  const getLabelIdByName = (labelName: string): string => {
    const foundId = tagSelectors.mapNameToIdValue(labelName, infos);
    return foundId || labelName;
  };

  const processed = labels
    .filter(([name]) => {
      if (name.toLowerCase() === "gps") return false;
      const labelId = getLabelIdByName(name);
      const userHasPermission = infos[labelId]?.details?.isHidden
        ? isSuperAdmin || infos[labelId]?.details?.author === authUser?.uid
        : true;
      return userHasPermission;
    })
    .map(([name, value]) => {
      const isCategory = isSpecialType(value, "isCategory");

      const friendlyLabel = (idOrName: string) => {
        if (currentLabels && currentLabels[idOrName]) return currentLabels[idOrName];
        if (infos?.[idOrName]?.details?.name) return infos[idOrName].details.name;
        return idOrName;
      };

      if (isCategory) {
        const categoryEntries = Object.entries(value)
          .filter(([key]) => {
            if (key === "isCategory") return false;
            const userHasPermission = infos[key]?.details?.isHidden
              ? isSuperAdmin || infos[key]?.details?.author === authUser?.uid
              : true;
            const isSerialNumber = key === "0" && value.isCategory;
            return userHasPermission && !isSerialNumber;
          })
          .map(([key, val]) => {
            const label = friendlyLabel(key);
            const mapped = tagSelectors.mapIdValueToNameValue(key, val, infos);
            const safeVal = mapped ? mapped[1] : val;
            const { text, date, files } = normalizeExtraInfoValue(safeVal);
            const labelIdForVal = key;
            const valueType = (infos[labelIdForVal]?.details as any)?.valueType;
            const formattedText = valueType === 'money' ? formatNumberText(text) : text;
            let formattedForCsv: any = formattedText;
            if (valueType === "money" && formattedText !== undefined && formattedText !== null) {
              formattedForCsv = `$${formattedText}`;
            }

            if (hasNewFormat) {
              const filesCell =
                files && files.length
                  ? (files[0].format === "pdf" ? "[PDF]" : "[Image]") +
                    (files.length > 1 ? `(+${files.length - 1})` : "")
                  : "-";
              return [label, formattedForCsv !== undefined && formattedForCsv !== null ? String(formattedForCsv) : "-", date || "-", filesCell || "-"];
            } else {
              const parts: string[] = [];
              if (formattedForCsv !== undefined && formattedForCsv !== null) parts.push(String(formattedForCsv));
              if (date) parts.push(date);
              if (files?.length) parts.push(files[0].format === "pdf" ? "[PDF]" : "[Image]");
              return [label, parts.length ? parts.join(", ") : "-"];
            }
          })
          .filter(Boolean);
        return categoryEntries;
      } else {
        const { text, date, files } = normalizeExtraInfoValue(value);
        const labelIdForVal = tagSelectors.mapNameToIdValue(name, infos) || name;
        const label = friendlyLabel(name);
        const valueType = (infos[labelIdForVal]?.details as any)?.valueType;
        const formattedText = valueType === 'money' ? formatNumberText(text) : text;
        let formattedForCsv: any = formattedText;
        if (valueType === "money" && formattedText !== undefined && formattedText !== null) {
          formattedForCsv = `$${formattedText}`;
        }
        if (hasNewFormat) {
          const filesCell =
            files && files.length
              ? (files[0].format === "pdf" ? "[PDF]" : "[Image]") +
                (files.length > 1 ? `(+${files.length - 1})` : "")
              : "-";
          return [[label, formattedForCsv !== undefined && formattedForCsv !== null ? String(formattedForCsv) : "-", date || "-", filesCell || "-"]];
        } else {
          const parts: string[] = [];
          if (formattedForCsv !== undefined && formattedForCsv !== null) parts.push(String(formattedForCsv));
          if (date) parts.push(date);
          if (files?.length) parts.push(files[0].format === "pdf" ? "[PDF]" : "[Image]");
          return [[label, parts.length ? parts.join(", ") : "-"]];
        }
      }
    })
    .flat()
    .filter(Boolean) as string[][];

  rows.push(...processed);

  const processRow = (row: string[]) =>
    row
      .map((field) => {
        const escaped = String(field).replace(/"/g, '""');
        return `"${escaped}"`;
      })
      .join(",");

  const csvData = rows.map(processRow).join("\n");
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

  const filename = "ExtraInfo.csv";
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
